import React, { useState, useEffect } from 'react';
import SelectFieldCheckbox from './SelectFieldCheckbox';
import PlaceHolderField from '../PlaceHolderField';
import ErrorMessage from '../../../ErrorMessage';
import TimeSheetEntryService from '../../../../Services/timeSheetEntryService';

/**
 * Office selector for an outsourced ACTIVITY (the "Outsourced Activity?" checkbox in the timesheet editor),
 * as opposed to the outsourced-employee BilledOfficeSelect (which is sourced from FTE entries).
 *
 * The backend narrows the offices by whatever the entry is labelled with (see
 * TimesheetEntryService.GetOutsourcedActivityOfficeOptionsAsync) and names the default. Emits
 * billedOfficeId + billedOfficeName together through changeOfEditedMultipItem (same pattern as
 * BilledOfficeSelect), so the parent receives both fields in one update.
 *
 * Extra props (forwarded by renderOutsourcedActivityOfficeSelect):
 *  - labelling: { leadId, clientId, opportunityId, clientFeeId, tpProjectId }; options reload when any of them change
 */
function OutsourcedActivityOfficeSelect({
  value,
  className,
  fieldName,
  onChange,
  filterCleared,
  changeOfEditedMultipItem,
  labelling,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const emitChange = (officeId, officeName) => {
    if (changeOfEditedMultipItem) {
      changeOfEditedMultipItem([
        { fieldName: fieldName, newValue: officeId },
        { fieldName: 'billedOfficeName', newValue: officeName },
      ]);
    } else {
      onChange(fieldName, officeId);
    }
  };

  // The labelling is a fresh object on every render, so the effect is keyed on its contents instead.
  const labellingKey = JSON.stringify(labelling || {});

  useEffect(() => {
    setLoading(true);
    TimeSheetEntryService.GetOutsourcedActivityOfficeOptions(labelling)
      .then((result) => {
        const officeOptions = (result.offices || []).map((o) => ({
          value: o.officeId,
          label: o.officeName,
        }));
        setOptions(officeOptions);
        setLoading(false);

        // Narrowing the labelling can drop the office that was already picked, and leaving a value the
        // dropdown no longer offers would be saved without ever being shown.
        if (value && !officeOptions.find((o) => String(o.value) === String(value))) {
          emitChange(null, '');
          return;
        }

        if (result.defaultOfficeId && !value) {
          const defaultOption = officeOptions.find((o) => o.value === result.defaultOfficeId);
          if (defaultOption) {
            emitChange(defaultOption.value, defaultOption.label);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        ErrorMessage.SetErrorMessage('OAOS74');
      });
  }, [labellingKey]);

  if (loading) {
    return (
      <PlaceHolderField
        className={className}
        fieldName={fieldName}
      />
    );
  }

  const selectValues = [{ value: '', label: 'Select office' }, ...options];

  return (
    <SelectFieldCheckbox
      value={value || ''}
      className={className || 'w-100 form-control form-control-sm'}
      fieldName={fieldName}
      onChange={(fName, newValue) => {
        const selected = options.find((o) => String(o.value) === String(newValue));
        emitChange(selected ? selected.value : null, selected ? selected.label : '');
      }}
      filterCleared={filterCleared}
      selectValues={selectValues}
      // Offices are a small, stable set, so the filter box is never worth its space. Without this the whole
      // office list sits right on the default threshold of 10 and the box would appear and disappear as the
      // labelling narrows the list.
      showSelectFilterAt={9999}
    />
  );
}

export default OutsourcedActivityOfficeSelect;
